import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import FloatingParticles from "./FloatingParticles";
const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";
export default function RecordsSummary() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  
  let user = null;
  try {
    const stored = localStorage.getItem("user");
    user = stored && stored !== "undefined" ? JSON.parse(stored) : null;
  } catch {
    user = null;
  }

  useEffect(() => {
    const fetchReports = async () => {
      const userId = user?.id || user?.userId;
      if (!userId) {
        setLoading(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/api/appointments/user/${userId}`);
        const appts = Array.isArray(res.data) ? res.data : [];
        const fathomReports = appts.filter(appt => appt && appt.fathom_summary);
        setReports(fathomReports);
      } catch (err) {
        console.error("Failed to load records summary:", err);
        toast.error("Failed to load records summary.");
      } finally {
        setLoading(false);
      }
    };
    fetchReports();
  }, [user?.id, user?.userId]);

  const parseJSON = (data) => {
    if (!data) return [];
    try {
      return typeof data === "string" ? JSON.parse(data) : data;
    } catch (e) {
      return [];
    }
  };

  return (
    <div className="min-h-[90vh] bg-gradient-to-br from-[#f0f9ff] to-[#e0f2fe] p-4 lg:p-8 flex justify-center items-start font-['Outfit'] relative overflow-hidden text-slate-800">
      <FloatingParticles />
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="w-full max-w-4xl relative z-10 space-y-8">
        
        {/* Header */}
        <div className="mb-8">
            <h1 className="text-4xl font-extrabold text-[#0c4a6e] tracking-tight">AI Diagnostic Echoes</h1>
            <p className="text-base text-slate-500 font-medium mt-2">Transcripts and actionable intelligence generated instantly post-visit.</p>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-48 bg-white/40 backdrop-blur-md rounded-[2rem] border border-blue-50">
            <div className="animate-pulse flex items-center gap-3 text-xl font-bold text-blue-500">
                <span className="text-3xl">🌀</span> Scanning records...
            </div>
          </div>
        ) : reports.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-16 text-center bg-white/40 backdrop-blur-md rounded-[2rem] border border-blue-100 shadow-xl shadow-blue-900/5">
            <span className="text-6xl mb-4 opacity-70">📄</span>
            <h3 className="text-2xl font-extrabold text-slate-700">No Intelligence Files Found</h3>
            <p className="text-sm font-medium text-slate-500 mt-2 max-w-sm mx-auto leading-relaxed w-full">
              Following your virtual sessions, Fathom AI will synthesize and deposit intelligent clinical summaries directly into this vault.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {reports.map((report) => {
              const topics = parseJSON(report.fathom_topics);
              const actionItems = parseJSON(report.fathom_action_items);
              
              return (
                <div key={report.appointment_id || report.id} className="bg-white/75 backdrop-blur-2xl p-8 sm:p-10 rounded-[2rem] shadow-2xl shadow-blue-900/10 border border-white/60 group hover:shadow-blue-900/20 transition-all duration-300">
                    
                  {/* Card Header Section */}
                  <div className="flex flex-col md:flex-row md:justify-between md:items-start border-b-2 border-slate-100/50 pb-5 mb-8 gap-4">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-[#e0f2fe] rounded-2xl flex items-center justify-center text-3xl shadow-sm border border-white group-hover:scale-110 transition-transform">
                            👨‍⚕️
                        </div>
                        <div>
                            <h3 className="text-2xl font-extrabold text-slate-800 tracking-tight">
                                Consult w/ {report.doctor_name || `Dr. ${report.doctor_email.split("@")[0]}`}
                            </h3>
                            <p className="text-sm font-semibold text-slate-500 mt-1 flex items-center gap-2 tracking-wide uppercase">
                                <span className="text-blue-400 text-lg leading-none">📆</span>
                                {new Date(report.scheduled_datetime).toLocaleString("en-IN", {
                                dateStyle: "long",
                                timeStyle: "short",
                                })}
                            </p>
                        </div>
                    </div>
                    <span className="bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-600 border border-emerald-100 text-[10px] font-extrabold px-3 py-1.5 rounded-xl uppercase tracking-widest self-start md:-mt-1 shadow-sm">
                      {report.status} Transcript
                    </span>
                  </div>

                  {/* Body Content */}
                  <div className="space-y-8">
                    
                    {/* The Summary Box */}
                    <div className="transform transition-transform">
                      <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-2 ml-1">
                        <span className="bg-blue-100 text-blue-500 rounded p-1 text-sm leading-none">🧠</span> Executive Synthesis
                      </h4>
                      <div className="text-slate-700 text-[15px] font-medium leading-relaxed whitespace-pre-wrap bg-slate-50/80 p-6 rounded-2xl border border-slate-200 border-l-4 border-l-blue-400 shadow-inner group-hover:bg-white transition-colors duration-500">
                        {report.fathom_summary}
                      </div>
                    </div>

                    {/* Columns: Topics & Actions */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 pt-2">
                        
                      {/* Topics */}
                      <div className="bg-emerald-50/40 p-5 rounded-2xl border border-emerald-100/50">
                        <h4 className="text-[11px] font-bold text-emerald-500/80 uppercase tracking-widest mb-4 flex items-center gap-2">
                          <span className="text-emerald-500 text-lg leading-none">📌</span> Diagnostic Points
                        </h4>
                        <ul className="space-y-3">
                          {topics.length > 0 ? (
                            topics.map((topic, i) => (
                              <li key={i} className="text-emerald-900 text-sm font-semibold flex items-start gap-3 bg-white/60 p-3 rounded-xl shadow-sm border border-emerald-50">
                                <span className="text-emerald-400 mt-0.5 text-lg leading-none">•</span>
                                <span className="pt-0.5">{topic}</span>
                              </li>
                            ))
                          ) : (
                            <li className="text-emerald-700/50 text-sm italic font-medium px-2">No key topics cataloged.</li>
                          )}
                        </ul>
                      </div>

                      {/* Action Items */}
                      <div className="bg-amber-50/40 p-5 rounded-2xl border border-amber-100/50">
                        <h4 className="text-[11px] font-bold text-amber-500/80 uppercase tracking-widest mb-4 flex items-center gap-2">
                          <span className="text-amber-500 text-lg leading-none">✅</span> Clinical Directives
                        </h4>
                        <ul className="space-y-3">
                          {actionItems.length > 0 ? (
                            actionItems.map((item, i) => (
                              <li key={i} className="text-amber-900 text-sm font-semibold flex items-start gap-3 bg-white/60 p-3 rounded-xl shadow-sm border border-amber-50">
                                <span className="text-amber-500 mt-0.5 text-lg leading-none">☐</span>
                                <span className="pt-0.5">{item}</span>
                              </li>
                            ))
                          ) : (
                            <li className="text-amber-700/50 text-sm italic font-medium px-2">No actionable criteria found.</li>
                          )}
                        </ul>
                      </div>
                      
                    </div>
                    
                    {/* Telemetry Footer */}
                    {report.fathom_transcript_url && (
                      <div className="pt-6 mt-2 border-t border-slate-100 w-full">
                        <a
                          href={report.fathom_transcript_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-center gap-2 w-full text-sm bg-gradient-to-r from-slate-800 to-slate-700 hover:from-[#0c4a6e] hover:to-blue-800 text-white font-bold py-4 px-6 rounded-xl transition-all shadow-lg hover:shadow-slate-500/30 hover:-translate-y-1"
                        >
                          <span className="text-xl leading-none">🔗</span> Access Full Encrypted Transcript
                        </a>
                      </div>
                    )}

                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}